import { BACKEND_URL } from "@/lib/backendUrl";
import { useCallback, useEffect, useState } from "react";
import {
  CheckCircle2, Copy, FileText, Loader2, Mail, MessageCircle, Plus, Send, ShieldQuestion, XCircle,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Chip, Empty, Kpi, Loading, PageHeader, Panel, Table, Td, Th } from "@/components/common/Chrome";
import { refOf, rowClick, useRowDetail } from "@/components/common/DetailSheet";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { dateTime } from "@/lib/format";

const OK = "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300";
const WARN = "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300";
const BAD = "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300";
const INFO = "border-primary/40 bg-primary/10 text-primary";
const STATUS = { sent: WARN, opened: WARN, in_progress: WARN, submitted: INFO, accepted: OK,
  rejected: BAD };
const STATUS_LABEL = { sent: "invited", opened: "link opened", in_progress: "started",
  submitted: "awaiting review", accepted: "accepted", rejected: "declined" };

const BLANK = { name: "", email: "", phone: "", country: "IN", donor_type: "individual",
  expected_gift: 0, message: "", send_email: true, send_whatsapp: true };

const F = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-[11px] text-muted-foreground">{label}</Label>{children}</div>
);

export default function OnboardingQueue({ embedded = false, onAccepted }) {
  const [regs, setRegs] = useState(null);
  const { can } = useAuth();
  const [state, setState] = useState(null);
  const detail = useRowDetail();
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState("");
  const [open, setOpen] = useState(false);
  const [share, setShare] = useState(null);
  const [review, setReview] = useState(null);
  const [note, setNote] = useState("");
  const API = BACKEND_URL;

  const load = useCallback(async () => {
    const [config, invites, subs, registrations] = await Promise.all([
      api.get("/onboarding/config"), api.get("/onboarding/invites"), api.get("/onboarding/submissions"),
      api.get("/donor/registrations"),
    ]);
    setState({ config: config.data, invites: invites.data, submissions: subs.data.items });
    setRegs(registrations.data);
  }, []);

  const decideRegistration = async (row, decision) => {
    try {
      const res = await api.post(`/donor/registrations/${row.id}/decide`, { decision });
      toast.success(res.data.message);
      await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  useEffect(() => { load().catch(() => setState(false)); }, [load]);

  const send = async () => {
    setBusy("send");
    try {
      const res = await api.post("/onboarding/invites", form);
      setShare(res.data);
      setForm(BLANK);
      setOpen(false);
      toast.success(res.data.email?.sent || res.data.whatsapp?.sent
        ? "Invitation delivered"
        : "Invitation created — share the link below");
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const decide = async (kind) => {
    setBusy(kind);
    try {
      const res = await api.post(`/onboarding/submissions/${review.id}/${kind}`, { note });
      toast.success(kind === "accept"
        ? `${res.data.name} added to the donor register${res.data.risk ? ` · ${res.data.risk.band} risk` : ""}`
        : "Submission rejected");
      setReview(null);
      setNote("");
      await load();
      if (kind === "accept" && onAccepted) onAccepted();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  if (!state) return <Loading label="Loading the onboarding desk" />;
  const { config, invites, submissions } = state;
  const c = invites.counters;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="SCR-023 · Donor self-onboarding"
        title={embedded ? "" : "Invite a donor to build their own verified profile"}
        description={embedded
          ? "Send a single-use secure link, the donor completes their profile and uploads the documents their country requires, then a reviewer accepts them into the register above."
          : "Finance and fundraising leads send a single-use secure link by email and WhatsApp. The donor completes their profile, uploads the identification documents required in their country of residence and records their consent — then a reviewer accepts it into the register, which screens and scores them automatically."}
        actions={can("onboarding", "invite") && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-invite-button"
                      className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                <Plus className="mr-1.5 h-4 w-4" /> Invite a donor
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg border-border bg-card">
              <DialogHeader><DialogTitle>Invite a prospective donor</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><F label="Full name or legal entity">
                  <Input value={form.name} data-testid="invite-name-input"
                         onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-muted/40" /></F></div>
                <F label="Email"><Input value={form.email} data-testid="invite-email-input"
                   onChange={(e) => setForm({ ...form, email: e.target.value })}
                   className="bg-muted/40 font-mono text-[12px]" /></F>
                <F label="Mobile (with country code)"><Input value={form.phone} data-testid="invite-phone-input"
                   onChange={(e) => setForm({ ...form, phone: e.target.value })}
                   placeholder="+91 98123 45678" className="bg-muted/40 font-mono text-[12px]" /></F>
                <F label="Country of residence">
                  <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
                    <SelectTrigger data-testid="invite-country-select" className="bg-muted/40"><SelectValue /></SelectTrigger>
                    <SelectContent>{config.countries.map((x) => (
                      <SelectItem key={x.code} value={x.code}>{x.label}</SelectItem>))}</SelectContent>
                  </Select>
                </F>
                <F label="Donor type">
                  <Select value={form.donor_type} onValueChange={(v) => setForm({ ...form, donor_type: v })}>
                    <SelectTrigger data-testid="invite-type-select" className="bg-muted/40"><SelectValue /></SelectTrigger>
                    <SelectContent>{["individual", "corporate", "foundation", "institutional"].map((x) => (
                      <SelectItem key={x} value={x}>{x}</SelectItem>))}</SelectContent>
                  </Select>
                </F>
                <div className="col-span-2"><F label="Personal message (optional)">
                  <Textarea rows={2} value={form.message} data-testid="invite-message-input"
                            onChange={(e) => setForm({ ...form, message: e.target.value })}
                            className="bg-muted/40 text-[12px]" /></F></div>
                <div className="col-span-2 space-y-2 rounded-xl border border-border p-3">
                  {[["send_email", "Send by email", config.providers.email, Mail],
                    ["send_whatsapp", "Send by WhatsApp", config.providers.whatsapp, MessageCircle]].map(
                    ([key, label, ready, Icon]) => (
                      <div key={key} className="flex items-center justify-between">
                        <span className="flex items-center gap-2 text-[12px] text-foreground">
                          <Icon className="h-3.5 w-3.5 text-primary" /> {label}
                          {!ready && <span className="text-[10px] text-muted-foreground">(provider not connected — share link instead)</span>}
                        </span>
                        <Switch checked={form[key]} data-testid={`invite-${key}-switch`}
                                onCheckedChange={(v) => setForm({ ...form, [key]: v })} />
                      </div>
                    ))}
                </div>
              </div>
              <Button onClick={send} data-testid="invite-send-button"
                      disabled={busy === "send" || !form.name || (!form.email && !form.phone)}
                      className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                {busy === "send" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  : <Send className="mr-2 h-4 w-4" />} Create and send
              </Button>
            </DialogContent>
          </Dialog>
        )}
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Kpi testId="kpi-invites" label="Invitations" value={c.sent} accent="sky" icon={Send} />
        <Kpi testId="kpi-invites-pending" label="Awaiting the donor" value={c.pending} accent="amber" />
        <Kpi testId="kpi-invites-submitted" label="Ready to review" value={c.submitted} accent="violet" icon={ShieldQuestion} />
        <Kpi testId="kpi-invites-accepted" label="Accepted" value={c.accepted} accent="emerald" icon={CheckCircle2} />
        <Kpi testId="kpi-invites-expired" label="Expired links" value={c.expired} accent="rose" />
      </div>

      {share && (
        <Panel testId="panel-share" kicker="Share" title={`Secure link for ${share.name}`}>
          <div className="space-y-3 p-4">
            <div className="flex flex-wrap items-center gap-2">
              <code className="flex-1 truncate rounded-lg bg-muted/50 px-3 py-2 font-mono text-[11px] text-foreground"
                    data-testid="share-link">{share.link}</code>
              <Button size="sm" variant="outline" data-testid="copy-link-button"
                      onClick={() => { navigator.clipboard.writeText(share.link); toast.success("Link copied"); }}
                      className="h-8 rounded-full border-border text-[11px]">
                <Copy className="mr-1 h-3.5 w-3.5" /> Copy
              </Button>
              {share.whatsapp_share_url && (
                <a href={share.whatsapp_share_url} target="_blank" rel="noreferrer" data-testid="whatsapp-share-link"
                   className="inline-flex h-8 items-center gap-1 rounded-full border border-emerald-500/40 px-3 text-[11px] text-emerald-700 transition-colors duration-150 hover:bg-emerald-500/10 dark:text-emerald-300">
                  <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                </a>
              )}
              {share.mailto_url && (
                <a href={share.mailto_url} data-testid="mailto-share-link"
                   className="inline-flex h-8 items-center gap-1 rounded-full border border-border px-3 text-[11px] text-foreground transition-colors duration-150 hover:bg-muted">
                  <Mail className="h-3.5 w-3.5" /> Email draft
                </a>
              )}
            </div>
            <div className="text-[11px] text-muted-foreground">
              Email: {share.email?.sent ? "delivered via Resend" : `not sent (${share.email?.reason})`} ·
              WhatsApp: {share.whatsapp?.sent ? "delivered via Mappstore" : `not sent (${share.whatsapp?.reason})`}
            </div>
          </div>
        </Panel>
      )}

      <Tabs defaultValue="review">
        <TabsList>
          <TabsTrigger value="review" data-testid="tab-review">Review queue ({c.submitted})</TabsTrigger>
          <TabsTrigger value="invites" data-testid="tab-invites">Invitations</TabsTrigger>
          <TabsTrigger value="registrations" data-testid="tab-registrations">
            Portal sign-ups{regs?.pending ? ` (${regs.pending})` : ""}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="review" className="mt-4">
          <Panel testId="panel-review" kicker="FR-DON-002" title="Submitted profiles awaiting verification">
            <Table testId="submissions-table"
                   head={<><Th>Donor</Th><Th>Country</Th><Th>Regime</Th><Th>Documents</Th><Th>Consent</Th>
                     <Th>Submitted</Th><Th>Status</Th><Th /></>}>
              {submissions.length === 0 && <Empty colSpan={8} label="No submitted profiles yet" />}
              {submissions.map((s) => (
                <tr key={refOf(s)} className="row-hover cursor-pointer" onClick={rowClick(() => detail.show(s, refOf(s)))} data-testid={`submission-row-${s.id}`}>
                  <Td><div className="font-medium text-foreground">{s.name}</div>
                    <div className="font-mono text-[10.5px] text-muted-foreground">{s.email || s.phone}</div></Td>
                  <Td className="font-mono text-[11px]">{s.country}</Td>
                  <Td><Chip className={INFO}>{s.requirement_regime || "—"}</Chip></Td>
                  <Td className="font-mono text-[11px]">{(s.documents || []).length}</Td>
                  <Td>{s.submission?.consent?.given
                    ? <Chip className={OK}>signed</Chip> : <Chip className={WARN}>pending</Chip>}</Td>
                  <Td className="font-mono text-[10.5px]">{s.submitted_at ? dateTime(s.submitted_at) : "—"}</Td>
                  <Td><Chip testId={`submission-status-${s.id}`} className={STATUS[s.status]}>{s.status}</Chip></Td>
                  <Td>
                    <Button size="sm" variant="outline" data-testid={`open-submission-${s.id}`}
                            onClick={() => setReview(s)}
                            className="h-7 rounded-full border-border px-2.5 text-[10px]">Open</Button>
                  </Td>
                </tr>
              ))}
            </Table>
          </Panel>
        </TabsContent>

        <TabsContent value="registrations" className="mt-4">
          <Panel testId="panel-registrations" kicker="Donor portal"
                 title="Supporters who asked for their own account"
                 actions={<Chip className={INFO}>
                   {regs?.signup_mode === "open" ? "accounts open straight away"
                     : regs?.signup_mode === "invite_only" ? "invitation only"
                       : "fundraising approves each one"}
                 </Chip>}>
            <Table testId="registrations-table"
                   head={<><Th>Supporter</Th><Th>Given to date</Th><Th>Asked</Th><Th>Status</Th>
                     <Th /></>}>
              {(regs?.items || []).length === 0 && (
                <Empty colSpan={5} label="Nobody is waiting for portal access" />
              )}
              {(regs?.items || []).map((r) => (
                <tr key={r.id} className="row-hover" data-testid={`registration-row-${r.id}`}>
                  <Td>
                    <div className="font-medium text-foreground">{r.name}</div>
                    <div className="font-mono text-[10.5px] text-muted-foreground">{r.email}</div>
                  </Td>
                  <Td className="font-mono text-[11px]">
                    {(r.total_given || 0).toLocaleString()} · {r.donation_count || 0} gift(s)
                  </Td>
                  <Td className="font-mono text-[10.5px]">
                    {r.portal_registered_at ? dateTime(r.portal_registered_at) : "—"}
                  </Td>
                  <Td>
                    <Chip className={r.portal_status === "pending" ? WARN : STATUS.rejected}>
                      {r.portal_status}
                    </Chip>
                  </Td>
                  <Td>
                    {r.portal_status === "pending" && regs?.may_decide && (
                      <div className="flex justify-end gap-1.5">
                        <Button size="sm" data-testid={`registration-approve-${r.id}`}
                                onClick={() => decideRegistration(r, "approve")}
                                className="h-7 rounded-full bg-primary px-2.5 text-[10px] text-primary-foreground hover:bg-primary/90">
                          Approve
                        </Button>
                        <Button size="sm" variant="outline"
                                data-testid={`registration-reject-${r.id}`}
                                onClick={() => decideRegistration(r, "reject")}
                                className="h-7 rounded-full border-border px-2.5 text-[10px]">
                          Decline
                        </Button>
                      </div>
                    )}
                  </Td>
                </tr>
              ))}
            </Table>
          </Panel>
        </TabsContent>

        <TabsContent value="invites" className="mt-4">
          <Panel testId="panel-invites" kicker="Invitations" title="Every link issued"
                 actions={(
                   <div className="flex flex-wrap gap-1.5">
                     <Chip className={WARN} data-testid="invite-counter-started">
                       {invites.counters.started} started, not submitted
                     </Chip>
                     <Chip className={BAD} data-testid="invite-counter-stuck">
                       {invites.counters.stuck} stuck
                     </Chip>
                     <Chip className={INFO}>{invites.counters.submitted} awaiting review</Chip>
                   </div>
                 )}>
            <Table testId="invites-table"
                   head={<><Th>Invitee</Th><Th>Channel</Th><Th>Country</Th><Th>Expires</Th><Th>Invited by</Th>
                     <Th>Status</Th><Th /></>}>
              {invites.items.length === 0 && <Empty colSpan={7} label="No invitations sent yet" />}
              {invites.items.map((i) => (
                <tr key={refOf(i)} className="row-hover cursor-pointer" onClick={rowClick(() => detail.show(i, refOf(i)))} data-testid={`invite-row-${i.id}`}>
                  <Td><div className="font-medium text-foreground">{i.name}</div>
                    <div className="font-mono text-[10.5px] text-muted-foreground">{i.email} {i.phone}</div>
                    {i.blocked_reason && (
                      <div className="mt-0.5 text-[10.5px] text-amber-700 dark:text-amber-300"
                           data-testid={`invite-blocked-${i.id}`}>
                        Stuck on: {i.blocked_reason}
                      </div>
                    )}</Td>
                  <Td className="text-[11px] text-muted-foreground">
                    {(i.delivery || []).slice(-1).map((d, n) => (
                      <span key={n}>{d.email?.sent ? "email ✓ " : ""}{d.whatsapp?.sent ? "whatsapp ✓" : ""}
                        {!d.email?.sent && !d.whatsapp?.sent ? "link only" : ""}</span>
                    ))}
                  </Td>
                  <Td className="font-mono text-[11px]">{i.country}</Td>
                  <Td className="font-mono text-[11px]">{i.expires_on}</Td>
                  <Td className="font-mono text-[10.5px] text-muted-foreground">{i.invited_by}</Td>
                  <Td><Chip className={STATUS[i.status]}>{STATUS_LABEL[i.status] || i.status}</Chip>
                    {i.last_activity_at && i.status !== "accepted" && (
                      <div className="mt-0.5 font-mono text-[10px] text-muted-foreground">
                        last seen {dateTime(i.last_activity_at)}
                      </div>
                    )}</Td>
                  <Td className="space-x-1.5">
                    <Button size="sm" variant="outline" data-testid={`copy-invite-${i.id}`}
                            onClick={() => { navigator.clipboard.writeText(i.link); toast.success("Link copied"); }}
                            className="h-7 rounded-full border-border px-2.5 text-[10px]">Copy link</Button>
                    {can("onboarding", "invite") && ["sent", "opened", "in_progress"].includes(i.status) && (
                      <Button size="sm" variant="outline" data-testid={`resend-invite-${i.id}`}
                              disabled={busy === `rs-${i.id}`}
                              onClick={async () => {
                                setBusy(`rs-${i.id}`);
                                try {
                                  const res = await api.post(`/onboarding/invites/${i.id}/resend`);
                                  setShare(res.data);
                                  toast.success("Invitation reissued");
                                  await load();
                                } catch (e) { toast.error(apiError(e)); }
                                setBusy("");
                              }}
                              className="h-7 rounded-full border-border px-2.5 text-[10px]">
                        {busy === `rs-${i.id}` ? <Loader2 className="h-3 w-3 animate-spin" /> : "Resend"}
                      </Button>
                    )}
                  </Td>
                </tr>
              ))}
            </Table>
          </Panel>
        </TabsContent>
      </Tabs>

      <Dialog open={Boolean(review)} onOpenChange={(o) => !o && setReview(null)}>
        <DialogContent className="max-w-2xl border-border bg-card" data-testid="review-dialog">
          <DialogHeader><DialogTitle>{review?.name} · verification</DialogTitle></DialogHeader>
          {review && (
            <div className="max-h-[70vh] space-y-4 overflow-y-auto pr-1">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {[["Country", review.country], ["Type", review.submission?.donor_type],
                  ["Tax ID", review.submission?.pan_or_tax_id || "—"], ["Email", review.email || "—"],
                  ["Mobile", review.phone || "—"], ["Regime", review.requirement_regime]].map(([k, v]) => (
                  <div key={k} className="rounded-xl border border-border bg-muted/25 p-2.5">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{k}</div>
                    <div className="truncate text-[12.5px] font-semibold text-foreground">{String(v ?? "—")}</div>
                  </div>
                ))}
              </div>

              {review.submission?.address && (
                <div className="rounded-xl border border-border p-3 text-[12px] text-muted-foreground">
                  <span className="text-foreground">Address:</span> {review.submission.address}
                </div>
              )}

              <div className="space-y-2">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Identification documents
                </div>
                {(review.documents || []).length === 0 && (
                  <div className="text-[12px] text-muted-foreground">No documents uploaded.</div>
                )}
                <div className="flex flex-wrap gap-2">
                  {(review.documents || []).map((d) => (
                    <a key={d.doc_id} href={`${API}/api/onboarding/documents/${d.doc_id}`} target="_blank"
                       rel="noreferrer" data-testid={`document-${d.key}`}
                       className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-[11px] text-foreground transition-colors duration-150 hover:border-primary/50 hover:bg-muted">
                      <FileText className="h-3.5 w-3.5 text-primary" /> {d.key} · {d.filename}
                    </a>
                  ))}
                </div>
              </div>

              <div className="space-y-1.5 rounded-xl border border-border p-3">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Consent declaration
                </div>
                {Object.entries(review.submission?.consent?.statements || {}).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-2 text-[12px]">
                    {v ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                      : <XCircle className="h-3.5 w-3.5 text-muted-foreground/60" />}
                    <span className={v ? "text-foreground" : "text-muted-foreground"}>{k.replace(/_/g, " ")}</span>
                  </div>
                ))}
                <div className="pt-1 font-mono text-[10.5px] text-muted-foreground">
                  Signed “{review.submission?.consent?.signed_name}” · {review.submission?.consent?.captured_at
                    ? dateTime(review.submission.consent.captured_at) : "—"}
                </div>
              </div>

              {review.status === "submitted" && can("onboarding", "write") && (
                <div className="space-y-2 rounded-xl border border-border p-3">
                  <Label className="text-[11px] text-muted-foreground">Reviewer note</Label>
                  <Textarea rows={2} value={note} data-testid="review-note-input"
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Documents checked against the register…" className="bg-muted/40 text-[12px]" />
                  <div className="flex gap-2">
                    <Button onClick={() => decide("accept")} disabled={busy === "accept"}
                            data-testid="accept-submission-button"
                            className="flex-1 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                      {busy === "accept" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Accept into the register
                    </Button>
                    <Button variant="outline" onClick={() => decide("reject")}
                            disabled={busy === "reject" || note.trim().length < 10}
                            data-testid="reject-submission-button"
                            className="rounded-full border-destructive/40 text-destructive hover:bg-destructive/10">
                      Reject
                    </Button>
                  </div>
                </div>
              )}
              {review.review_note && (
                <div className="rounded-xl border border-primary/30 bg-primary/5 p-2.5 text-[12px]" data-testid="review-outcome">
                  <span className="font-semibold text-foreground">{review.status}</span> by {review.reviewed_by} — {review.review_note}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {detail.sheet}
    </div>
  );
}
